import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { FC, Suspense, useMemo, useState } from 'react';
import { useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { DashboardSkillChallengeAttemptedPerStudentQuery } from '../../../graphQLOperations/__generated__/DashboardSkillChallengeAttemptedPerStudentQuery.graphql';
import {
  getSkillAssessmentReportDetail,
  getSkillChallengeAttemptedPerStudent,
} from '../../../graphQLOperations/Dashboard';
import { selectedTeacherDashboardFilter } from '../../../redux/reducers/teacherDashboardFiltersSlice';
import { useAppSelector } from '../../../redux/hooks';
import {
  getCurrentWeek,
  splitDateByParams,
  SplitTargetType,
} from '../../../businessLogicService/dateHandlerForTeacherDashboard';
import SkillChallengeAttemptedTableHeader from './SkillChallengeAttemptedTableHeader';
import { DashboardSkillAssessmentReportDetalQuery } from '../../../graphQLOperations/__generated__/DashboardSkillAssessmentReportDetalQuery.graphql';
import SkillChallengeAttemptedDialog from './SkillChallengeAttemptedDialog';
import { PrimeReactTableSort } from '../../../models/PrimeReactTableSort';
import { FilterOptionValues } from '../TeacherDashboard';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Roles } from '../../../models/User';
import { StudentNameLink } from '../../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../../analytics/dataMasking';
import TeacherDashboardWidgetEmpty, { TeacherDashboardWidgetType } from '../TeacherDashboardWidgetEmpty';

interface StudentReportData {
  userId: number;
  publicUserId: string;
  name: string;
  // The table sorts on these two fields, so they have to be on the row even though
  // only the composed `name` is rendered.
  firstName: string;
  lastName: string;
  passed: number;
  notPassed: number;
  total: number;
}

const enum AssessmentReportDetailType {
  NOT_PASSED = 'NOT_PASSED',
  PASSED = 'PASSED',
  TOTAL = 'TOTAL',
}

const AssessmentReportDetailParsed = {
  NOT_PASSED: 'Not Passed',
  PASSED: 'Passed',
  TOTAL: 'Total',
};

interface Props {
  filterOption: FilterOptionValues;
}

export const SkillChallengeAttemptedTable: FC<Props> = ({ filterOption }) => {
  const userRedux = useAppSelector(selectUser);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const [attemptedDialogVisible, setAttemptedDialogVisible] = useState(false);
  const [date, setDate] = useState<string>(getCurrentWeek());
  const [dialogData, setDialogData] = useState<{ name: string; headerType: string; publicUserId: string }>({
    name: '',
    headerType: '',
    publicUserId: '',
  });
  const [sort, setSort] = useState<PrimeReactTableSort>({ field: 'lastName', order: 1 });
  const isSiteAdmin = userRedux?.roles.some(role => role.name === Roles.SITE_ADMIN);
  const { studentSkillAssessmentsReport } = useLazyLoadQuery<DashboardSkillChallengeAttemptedPerStudentQuery>(
    getSkillChallengeAttemptedPerStudent,
    {
      filter: {
        siteIds: isSiteAdmin && teacherDashboardFilters.sites ? teacherDashboardFilters.sites.map(st => st.id) : null,
        targetGroupIds:
          filterOption === FilterOptionValues.ROADMAP && isSiteAdmin && teacherDashboardFilters.roadmaps
            ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
            : null,
        classIds:
          (filterOption === FilterOptionValues.CLASSES || !isSiteAdmin) && teacherDashboardFilters.classes?.length
            ? teacherDashboardFilters.classes.map(cl => cl.id)
            : null,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades?.map(gd => gd.id) : null,
        reportFromDate: splitDateByParams(date, SplitTargetType.FROM),
        reportToDate: splitDateByParams(date, SplitTargetType.TO),
      },
    },
    { fetchPolicy: 'network-only' },
  );
  const reportNodes = studentSkillAssessmentsReport?.nodes;

  const [queryReference, loadQuery] =
    useQueryLoader<DashboardSkillAssessmentReportDetalQuery>(getSkillAssessmentReportDetail);

  const loadQueryHandle = (student: StudentReportData, assessmentReportDetailType: AssessmentReportDetailType) => {
    loadQuery(
      {
        filter: {
          userId: `UserNode:${student.userId}`,
          reportFromDate: splitDateByParams(date, SplitTargetType.FROM),
          reportToDate: splitDateByParams(date, SplitTargetType.TO),
          assessmentReportDetailType: assessmentReportDetailType,
        },
      },
      { fetchPolicy: 'network-only' },
    );
    setDialogData({
      headerType: AssessmentReportDetailParsed[assessmentReportDetailType],
      name: student.name,
      publicUserId: student.publicUserId,
    });
    setAttemptedDialogVisible(true);
  };

  const bodyTemplate = (student: StudentReportData, assessmentReportDetailType: AssessmentReportDetailType) => {
    const isClickable =
      (assessmentReportDetailType === AssessmentReportDetailType.PASSED && student.passed > 0) ||
      (assessmentReportDetailType === AssessmentReportDetailType.NOT_PASSED && student.notPassed > 0) ||
      (assessmentReportDetailType === AssessmentReportDetailType.TOTAL && student.total > 0);

    return (
      <a
        className={`${
          isClickable ? 'text-primary cursor-pointer' : 'tw-text-black tw-cursor-default'
        } bg-transparent border-none p-0 font-inherit ph-no-capture`}
        type="button"
        onClick={() => isClickable && loadQueryHandle(student, assessmentReportDetailType)}
        aria-label={`${student.name}: ${AssessmentReportDetailParsed[assessmentReportDetailType]} attempts`}
      >
        {assessmentReportDetailType === AssessmentReportDetailType.PASSED && student.passed}
        {assessmentReportDetailType === AssessmentReportDetailType.NOT_PASSED && student.notPassed}
        {assessmentReportDetailType === AssessmentReportDetailType.TOTAL && student.total}
      </a>
    );
  };

  const bodyNameTemplate = (student: StudentReportData) => (
    <StudentNameLink studentId={String(student.userId)} classIds={teacherDashboardFilters?.classes?.map(cl => cl.id)}>
      <MaskedStudentData type={PiiTypes.FullName} student={student}>
        {student.name}
      </MaskedStudentData>
    </StudentNameLink>
  );

  // Derived while rendering instead of mirrored into state: the rows and the empty flag are both
  // pure functions of the query result. Copying them into state through an effect meant that once
  // a week reported empty nothing ever cleared the flag, so moving to a previous week that does
  // have data kept rendering the empty widget.
  const students = useMemo<StudentReportData[]>(
    () =>
      reportNodes?.map(node => ({
        userId: node.userId,
        publicUserId: node.publicUserId,
        name: `${node.firstName} ${node.lastName}`,
        firstName: node.firstName || '',
        lastName: node.lastName || '',
        passed: node.passedSkillChallenges,
        notPassed: node.notPassedSkillChallenges,
        total: node.totalSkillChallenges,
      })) ?? [],
    [reportNodes],
  );

  const hasNoAttempts = !students.length || students.every(student => student.total === 0);

  const KeyboardSortableHeader: FC<{ label: string }> = ({ label }) => (
    <span
      tabIndex={0}
      role="button"
      className="p-column-title"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).closest('th')?.click();
        }
      }}
    >
      {label}
    </span>
  );

  return (
    <>
      {studentSkillAssessmentsReport && (
        <Card
          id="SkillChallengesAttemptedWeekly"
          header={<SkillChallengeAttemptedTableHeader date={date} setDate={setDate} />}
          className="col-12 mt-2"
        >
          {hasNoAttempts ? (
            <TeacherDashboardWidgetEmpty type={TeacherDashboardWidgetType.SKILL_CHALLENGE_ATTEMPTS} noHeader={true} />
          ) : (
            <DataTable
              filterDisplay="row"
              value={students}
              rows={5}
              rowsPerPageOptions={[5, 10, 25]}
              paginator
              stripedRows
              emptyMessage="No students found."
              scrollable
              sortOrder={sort.order}
              sortField={sort.field}
              onSort={e => {
                setSort({
                  order: e.sortOrder ? e.sortOrder : 1,
                  field: e.sortField,
                });
              }}
              scrollHeight="600px"
              className="w-full"
              paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            >
              <Column
                sortable
                body={(e: StudentReportData) => bodyNameTemplate(e)}
                field="lastName"
                header={<KeyboardSortableHeader label="Name" />}
                frozen
              />
              <Column
                sortable
                body={e => bodyTemplate(e, AssessmentReportDetailType.PASSED)}
                field="passed"
                header={<KeyboardSortableHeader label="Passed" />}
                frozen
              />
              <Column
                sortable
                body={e => bodyTemplate(e, AssessmentReportDetailType.NOT_PASSED)}
                field="notPassed"
                header={<KeyboardSortableHeader label="Not Passed" />}
                frozen
              />
              <Column
                sortable
                body={e => bodyTemplate(e, AssessmentReportDetailType.TOTAL)}
                field="total"
                header={<KeyboardSortableHeader label="Total" />}
                frozen
              />
            </DataTable>
          )}
        </Card>
      )}
      {attemptedDialogVisible && (
        <Suspense fallback={null}>
          <SkillChallengeAttemptedDialog
            visible={attemptedDialogVisible}
            setVisible={setAttemptedDialogVisible}
            queryReference={queryReference}
            dialogData={dialogData}
          />
        </Suspense>
      )}
    </>
  );
};
